<script setup lang="ts">
import { onClickOutside, onKeyStroke, useEventListener } from '@vueuse/core'

type PopoverProps = {
  placement?: PopoverSide | 'auto'
  align?: PopoverAlign
  autoHide?: number
}

const props = withDefaults(defineProps<PopoverProps>(), {
  placement: 'bottom',
  align: 'end',
  autoHide: undefined,
})

type DismissReason = 'auto-hide' | 'escape' | 'outside' | 'trigger'

const emit = defineEmits<{ dismiss: [reason: DismissReason] }>()

const isOpen = defineModel<boolean>('open', { default: false })
const rootRef = ref<HTMLElement | null>(null)
const contentRef = ref<HTMLElement | null>(null)
const autoPlacement = ref<{ side: PopoverSide; align: PopoverAlign }>()
const side = computed(() => (props.placement === 'auto' ? (autoPlacement.value?.side ?? 'bottom') : props.placement))
const align = computed(() => (props.placement === 'auto' ? (autoPlacement.value?.align ?? props.align) : props.align))
let hideTimer: ReturnType<typeof setTimeout> | undefined

const updateAutoPlacement = () => {
  if (props.placement !== 'auto' || !rootRef.value || !contentRef.value) return
  const { width, height } = contentRef.value.getBoundingClientRect()
  autoPlacement.value = pickPopoverPlacement({
    trigger: rootRef.value.getBoundingClientRect(),
    content: { width, height },
    viewport: { width: window.innerWidth, height: window.innerHeight },
    align: props.align,
    inset: parseFloat(getComputedStyle(contentRef.value).getPropertyValue('--popover-inset')) || 0,
  })
}

let placementFrame = 0
const scheduleAutoPlacement = () => {
  if (placementFrame || props.placement !== 'auto' || !isOpen.value) return
  placementFrame = requestAnimationFrame(() => {
    placementFrame = 0
    updateAutoPlacement()
  })
}

watch(contentRef, updateAutoPlacement, { flush: 'post' })
useEventListener('resize', scheduleAutoPlacement, { passive: true })
useEventListener('scroll', scheduleAutoPlacement, { passive: true, capture: true })

const close = () => {
  isOpen.value = false
}

const dismiss = (reason: DismissReason) => {
  if (!isOpen.value) return
  close()
  emit('dismiss', reason)
}

const stopAutoHide = () => {
  clearTimeout(hideTimer)
  hideTimer = undefined
}

const startAutoHide = () => {
  stopAutoHide()
  if (import.meta.client && props.autoHide && isOpen.value) {
    hideTimer = setTimeout(() => dismiss('auto-hide'), props.autoHide)
  }
}

watch(isOpen, (open) => (open ? startAutoHide() : stopAutoHide()), { immediate: true })
onBeforeUnmount(() => {
  stopAutoHide()
  cancelAnimationFrame(placementFrame)
})

onClickOutside(contentRef, (event) =>
  dismiss(event.target instanceof Node && rootRef.value?.contains(event.target) ? 'trigger' : 'outside'),
)
onKeyStroke('Escape', () => dismiss('escape'))
</script>

<template>
  <div ref="rootRef" class="popover">
    <slot name="trigger" />
    <div role="status" data-testid="popover__live-region">
      <Transition name="popover-fade">
        <div
          v-if="isOpen"
          ref="contentRef"
          class="popover__content"
          :class="[`popover__content--${side}`, `popover__content--${align}`]"
          data-testid="popover__content"
          @mouseenter="stopAutoHide"
          @mouseleave="startAutoHide"
          @focusin="stopAutoHide"
          @focusout="startAutoHide"
        >
          <slot :close="close" />
        </div>
      </Transition>
    </div>
  </div>
</template>

<style lang="postcss">
.popover {
  display: inline-block;
  position: relative;
}

.popover__content {
  --popover-arrow-size: 0.6rem;

  position: absolute;
  z-index: 2;
  width: var(--popover-width, max-content);
  padding: var(--space-40);
  color: var(--text-primary);
  font-size: var(--text-small);
  line-height: 1.4;
  border: 1px solid var(--border-neutral-20);
  border-radius: var(--border-radius-8);
  background: var(--ui-neutral-00);
  box-shadow:
    0 2px 6px 0 rgb(0 0 0 / 14%),
    0 6px 16px -4px rgb(0 0 0 / 10%);
  animation: fade-in 0.4s cubic-bezier(0.25, 0, 0.3, 1);

  &.popover-fade-leave-active {
    animation: fade-out 0.4s cubic-bezier(0.25, 0, 0.3, 1) forwards;
  }

  &::before {
    --popover-arrow-edge: calc(var(--popover-arrow-size) / -2 - 1px);

    content: '';
    position: absolute;
    width: var(--popover-arrow-size);
    height: var(--popover-arrow-size);
    background: inherit;
    border-top: 1px solid var(--border-neutral-20);
    border-left: 1px solid var(--border-neutral-20);
  }

  /* The bordered corner of the rotated square is the arrow tip; scale flattens it into a shallow point */
  &.popover__content--bottom {
    top: 100%;
    &::before {
      top: var(--popover-arrow-edge);
      transform: scaleY(0.6) rotate(45deg);
    }
  }

  &.popover__content--top {
    bottom: 100%;
    &::before {
      bottom: var(--popover-arrow-edge);
      transform: scaleY(0.6) rotate(225deg);
    }
  }

  &.popover__content--right {
    left: 100%;
    &::before {
      left: var(--popover-arrow-edge);
      transform: scaleX(0.6) rotate(-45deg);
    }
  }

  &.popover__content--left {
    right: 100%;
    &::before {
      right: var(--popover-arrow-edge);
      transform: scaleX(0.6) rotate(135deg);
    }
  }

  &:is(.popover__content--bottom, .popover__content--top) {
    &.popover__content--start {
      left: var(--popover-inset, 0);
      &::before {
        left: var(--popover-arrow-offset, var(--space-50));
      }
    }

    &.popover__content--end {
      right: var(--popover-inset, 0);
      &::before {
        right: var(--popover-arrow-offset, var(--space-50));
      }
    }

    &.popover__content--center {
      left: 50%;
      translate: -50% 0;
      &::before {
        left: calc(50% - var(--popover-arrow-size) / 2);
      }
    }
  }

  &:is(.popover__content--left, .popover__content--right) {
    &.popover__content--start {
      top: var(--popover-inset, 0);
      &::before {
        top: var(--popover-arrow-offset, var(--space-50));
      }
    }

    &.popover__content--end {
      bottom: var(--popover-inset, 0);
      &::before {
        bottom: var(--popover-arrow-offset, var(--space-50));
      }
    }

    &.popover__content--center {
      top: 50%;
      translate: 0 -50%;
      &::before {
        top: calc(50% - var(--popover-arrow-size) / 2);
      }
    }
  }
}
</style>
